import { useEffect, type JSX } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { OrderMeshLogo } from '@components/composition/OrderMeshLogo';
import { SignInForm } from '../components/SignInForm';
import { useIsAuthenticated } from '@hooks/auth';

interface LocationStateWithRedirect {
  from?: { pathname: string };
}

export function SignInPage(): JSX.Element {
  const { t } = useTranslation(['auth', 'navigation']);
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthenticated = useIsAuthenticated();

  const redirectTo = (location.state as LocationStateWithRedirect | undefined)?.from?.pathname ?? '/';

  // If the user lands here already signed in (e.g. back-button), bounce them home.
  useEffect(() => {
    if (isAuthenticated) navigate(redirectTo, { replace: true });
  }, [isAuthenticated, navigate, redirectTo]);

  return (
    <main className="flex min-h-screen items-center justify-center bg-background px-4 py-12">
      <div className="w-full max-w-md">
        <div className="mb-6 flex items-center gap-2">
          {/* Same OrderMesh wordmark the app shell topbar uses (not the old
              "OM" placeholder square) so login matches the rest of the app. */}
          <OrderMeshLogo className="h-4 w-auto text-foreground" role="img" data-testid="signin-logo" />
          <span className="text-sm text-muted-foreground">{t('navigation:brand.suffix')}</span>
        </div>

        <Card>
          <CardHeader>
            <CardTitle data-testid="signin-title">{t('signIn.title')}</CardTitle>
            <CardDescription>{t('signIn.subtitle')}</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-6">
            <SignInForm onSuccess={() => navigate(redirectTo, { replace: true })} />
            <div className="text-center text-sm">
              <Link
                to="/forgot-password"
                className="text-primary underline-offset-4 hover:underline"
                data-testid="signin-forgot-password-link"
              >
                {t('signIn.forgotPassword')}
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </main>
  );
}
